import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { ApiError, apiJson } from '@/lib/apiClient';

import { adminVehiclesQueryKey } from './useAdminVehicles';

/**
 * SLICE 56b — edit a vehicle's customer-facing IDENTITY (plate, name/label, body
 * type). Super-admin only; the customer app is deliberately read-only for these
 * (absence over disabled), so this is the ONLY place they can be changed after
 * provisioning. Mirrors slice 55's tenant-rename hook (`useEditTenantName`).
 *
 * Wire contract (seam S1), mirroring `api/src/modules/admin/routes.ts`:
 *   PATCH /api/admin/vehicles/:vehicleId   body { plate?, label?, model?, vehicleType? }
 *     200 { vehicleId, plate, label, model, vehicleType }  — edited (or a no-op merge)
 *     400 { error: ... }                            — bad body / bad enum / unknown key
 *     404 { error: 'not_found' }                    — malformed uuid OR unknown vehicle
 *
 * A 200 with a JSON body, so `apiJson` (NOT `apiVoid`). The body is a PARTIAL update:
 * the dialog sends only the fields that changed, so a stray field can never be sent
 * (the route is `.strict()` and would 400 one anyway). `label: ''` is the blank-it
 * intent and the server maps it to null.
 *
 * On success the global vehicle list is invalidated so the row — and the detail
 * dialog reading it — reflects the new plate/label/type without a manual refetch. A
 * `vehicleType` change swaps the map glyph everywhere the vehicle is drawn.
 *
 * 🔴 Traccar's own device name is deliberately NOT renamed by this route (mirrors the
 * slice-55 "group is not renamed" decision) — this is a pure raad-schema edit.
 */

export type EditVehicleFailure = 'not_found' | 'invalid_body' | 'unknown';

export function classifyEditVehicleError(err: unknown): EditVehicleFailure {
  if (err instanceof ApiError) {
    if (err.status === 404) return 'not_found';
    if (err.status === 400) return 'invalid_body';
  }
  return 'unknown';
}

/** The partial patch — every field optional; only changed ones are sent. `label` and
 *  `model` may be `''` (blank them → server stores null).
 *
 *  SLICE 83 — `reportPeriodSec` is the device's configured check-in period (5–3600 s),
 *  or `null` to fall back to the 300 s default. SEAM S3: the API's `EditVehicleBodyZ`
 *  must accept it and the live directory must invalidate after the write (backend half). */
export interface EditVehiclePatch {
  plate?: string;
  label?: string;
  model?: string;
  vehicleType?: string;
  reportPeriodSec?: number | null;
}

export interface EditVehicleVars {
  vehicleId: string;
  patch: EditVehiclePatch;
}

export interface EditVehicleResult {
  vehicleId: string;
  plate: string;
  label: string | null;
  model: string | null;
  vehicleType: string | null;
}

export function useEditVehicle(): UseMutationResult<EditVehicleResult, Error, EditVehicleVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vehicleId, patch }: EditVehicleVars) =>
      apiJson<EditVehicleResult>(`/api/admin/vehicles/${encodeURIComponent(vehicleId)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminVehiclesQueryKey });
    },
  });
}
